import { BLOCKS } from '@contentful/rich-text-types';
import get from 'lodash.get';
import React from 'react';

import Carousel from '../components/Common/Carousel/Carousel';
import ContentCard from '../components/Common/ContentCard';
import DynamicForm from '../components/Common/DynamicForm/Form';
import FullSectionCard from '../components/Common/FullSectionCard';
import { ArrowLink } from '../components/Common/Links';
import MarketoForm from '../components/Common/MarketoForm/MarketoForm';
import PageSection from '../components/Common/PageSection';
import * as embeddedEntries from '../components/Common/RichText/EmbeddedEntries';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import Testimonial from '../components/DXI/Testimonial';
import CallToAction from '../components/Generic/CallToAction';
import GroupedContent from '../components/Generic/GroupedContent';
import HalfAndHalfContent from '../components/Generic/HalfAndHalfContent';
import HalfBGContentCard from '../components/Generic/HalfBGContentCard';
import LogoSection from '../components/Generic/LogoSection';
import ProgramContentCard from '../components/Generic/ProgramContentCard';
import SessionList from '../components/Generic/sessionList';
import SparkThreeUp from '../components/Spark/SparkThreeUp';

export const sectionBuilder = (
  page,
  styles,
  pageType = '',
  pageContext = {},
  scrollRef = { current: undefined },
  onCtaClick = () => null
) =>
  page.pageSections?.map((section, i) => {
    const isCampaignLP = pageType === 'campaign';
    switch (section.__typename) {
      case 'ContentfulContentCard': {
        if (section.titleIsSectionHeader) {
          let location = section.headingLocation === 'Left' ? styles.headingLeft : '';
          return (
            <PageSection
              moderate
              className={[section.backgroundColor === 'Grey' ? styles.grey : '', styles.sectionHeaderWrap].join(' ')}
              key={`title-${i}`}
            >
              <div className={[styles.sectionHeader, location].join(' ')}>
                <h2>{section.title}</h2>
              </div>
            </PageSection>
          );
        }
        let imageSide = section.imageLocation === true ? 'Left' : 'Right';
        if (section.imageLocation === undefined) {
          imageSide = i > 0 && i % 2 === 0 ? 'Left' : 'Right';
        }

        switch (section.imageStyle) {
          case 'Small Centered': {
            return <ProgramContentCard section={section} imageSide={imageSide} key={i} limitImageSize={true} />;
          }
          case 'Centered With Background': {
            return <HalfBGContentCard section={section} imageSide={imageSide} />;
          }
          case 'Below Content with Bleed': {
            return (
              <PageSection slim wide fullBleed>
                <div className={styles.stacked}>
                  <div className={styles.copy}>
                    <h2>{section.title}</h2>
                    <RichTextConverter copy={section.copy} />
                  </div>
                  {section.mainImage && (
                    <>
                      <img src={section.mainImage?.file.url} alt={section.mainImage?.title} />
                    </>
                  )}
                </div>
              </PageSection>
            );
          }
          default:
            break;
        }

        /*if (section.imageStyle === 'Half & Half') {
          return <HalfAndHalfContent section={section} imageSide={imageSide} />;
        }*/

        const backgroundColors = {
          Grey: styles.grey,
          Blue: styles.blue,
          Purple: styles.purple,
          'Purple / Pink Gradient': styles.purplePinkGradient,
        };

        return (
          <ContentCard
            key={i}
            slim
            adjustForTallImage={section.adjustForTallImage}
            heading={section.titleIsSectionHeader ? '' : section.title}
            image={section.mainImage}
            wistiaUrl={section.wistiaUrl}
            copy={section.copy}
            ctaUrl={section.ctaUrl}
            ctaLabel={section.ctaLabel}
            newWindow={section.ctaOpensNewWindow}
            imageBleed={section.imageStyle === 'Bleed' || isCampaignLP}
            imageSide={imageSide}
            limitImageHeight
            className={[backgroundColors[section.backgroundColor] || ''].join(' ')}
            curve={section.hasCurve}
          />
        );
      }
      case 'ContentfulSectionCallout': {
        return (
          <FullSectionCard
            key={i}
            label={section.ctaLabel}
            heading={section.heading}
            backColor={section.backgroundOverlayColor}
            imageSrc={section.mainImage?.file?.url ? section.mainImage.file.url : ''}
            backImageSrc={section.backgroundImage ? section.backgroundImage.file.url : ''}
            link={section.ctaUrl}
            position={section.position}
            section={section}
          />
        );
      }
      case 'ContentfulJsonData': {
        return <SessionList section={section} />;
      }
      case 'ContentfulTestimonial': {
        let bgColor;
        switch (section.backgroundColor) {
          case 'Grey': {
            bgColor = styles.grey;
            break;
          }
          case 'Light Blue': {
            bgColor = styles.blue;
            break;
          }
          case 'Light Purple': {
            bgColor = styles.purple;
            break;
          }
          default: {
            bgColor = styles.white;
            break;
          }
        }

        return (
          <PageSection key={i} slim className={bgColor}>
            <Testimonial logoClass={styles.logo} logo={section.logo} body={get(section, 'quote.quote')} />
          </PageSection>
        );
      }
      case 'ContentfulMarketoForm': {
        const options = {
          renderNode: {
            [BLOCKS.EMBEDDED_ENTRY]: (node) => {
              const type = get(node, 'data.target.internal.type');
              if (type === 'ContentfulMediaGroup') {
                return embeddedEntries.simpleMediaGroup(node);
              }
            },
          },
        };
        let form = <MarketoForm formId={section.marketoFormId} />;
        if (section.useDynamicForms && section.marketoForm?.childrenMarketoFormField?.length > 0) {
          form = (
            <DynamicForm
              form={section.marketoForm}
              programName={section.marketoForm.programName}
              successMessage={section.successMessage}
              followUpForms={section.followUpFormsData}
            />
          );
        }
        return (
          <>
            {page.headingCtaScrollsToForm && <div ref={scrollRef} id={'marketo-form'} />}
            <PageSection moderate className={styles.grey} key={i}>
              <div className={styles.form}>
                <div className={styles.formCopy}>
                  <RichTextConverter copy={get(section, 'formCopy')} options={options} />
                </div>
                {form}
              </div>
            </PageSection>
          </>
        );
      }
      case 'ContentfulGroupedContent': {
        if (pageContext.isSpark && section.type === 'Template Standard') {
          return <SparkThreeUp section={section} key={i} />;
        }
        return (
          <GroupedContent
            key={i}
            section={section}
            i={i}
            noPad={isCampaignLP}
            wide={isCampaignLP}
            fullImage={isCampaignLP}
          />
        );
      }
      case 'ContentfulCarousel': {
        return (
          <PageSection key={i} moderate>
            <Carousel
              heading={section.heading}
              eyebrowText={section.eyebrowText}
              accentColor={section.accentColor}
              items={section.cards}
              onCta={onCtaClick}
              ctaLabel={section.ctaLabel}
              ctaUrl={section.ctaUrl}
            />
          </PageSection>
        );
      }
      case 'ContentfulCallToAction': {
        const backgroundColors = {
          Grey: styles.grey,
          'Dark Blue Circle Pattern': styles.darkBlue,
          White: styles.white,
          'Light Grey': styles.lightGrey,
          'Purple / Pink': styles.purplePink,
        };

        const bgColor = backgroundColors[section.backgroundColor];

        let fullSection = [];

        if (!section.image && !section.attachment) {
          return (
            <PageSection slim key={i} className={backgroundColors[section.backgroundColor] || styles.white}>
              <CallToAction section={section} />
            </PageSection>
          );
        }
        // TODO: Is this being used?
        fullSection.push(
          <ContentCard
            key={i}
            heading={section.title}
            backgroundStyle={bgColor || styles.white}
            isSpark={pageContext.isSpark}
            image={section.image}
            copy={get(section, 'description.description')}
            imageBleed={false}
            imageSide={'Right'}
            ctaUrl={section.url || get(section, 'attachment.file.url')}
            ctaIsDownload={get(section, 'attachment.file.url', false)}
            ctaLabel={section.ctaLabel}
            newWindow={section.newWindow}
            limitImageHeight
            moderate
          />
        );
        return fullSection;
      }
      case 'ContentfulHalfContentSection': {
        return (
          <HalfAndHalfContent key={i} section={section} extended={!section.backgroundOverlayColor && isCampaignLP} />
        );
      }
      case 'ContentfulMediaGroup': {
        if (section.type === 'Squares') {
          return <LogoSection key={i} section={section} />;
        }
        /*
        TODO: Legacy -
         1. Update all logos to square in contentful
         2. Set them to template standard
         3. Update "Squares" to template standard
         4. Remove section below
         */
        const bgColor = section.backgroundColor === 'Grey' ? styles.grey : styles.white;
        let flexAmount;

        switch (section.amountPerRow) {
          case 5:
            flexAmount = '18%';
            break;

          case 4:
            flexAmount = '20%';
            break;

          default:
            flexAmount = '12%';
            break;
        }

        return (
          <PageSection key={i} moderate className={[bgColor].join(' ')}>
            <div className={styles.logos}>
              <h3>{section.title}</h3>
              <div className={styles.logoGrid} style={{ '--amountPerRow': section.amountPerRow || 6 }}>
                {section.media.map((logo, j) => {
                  return (
                    <div className={styles.logo} style={{ '--amountFlex': flexAmount }} key={j}>
                      <img src={logo.file.url} alt={logo.title} />
                    </div>
                  );
                })}
              </div>
              {section.ctaLabel && (
                <div className={styles.logoCta}>
                  <ArrowLink label={section.ctaLabel} url={section.ctaUrl} />
                </div>
              )}
            </div>
          </PageSection>
        );
      }

      default: {
        return '';
      }
    }
  });
